import { msg } from "@lingui/core/macro";
import {
  createIngredientMutation,
  deleteIngredientMutation,
  foodCostPercent,
  formatMoney,
  getIngredientsQuery,
  getProductsQuery,
  getRecipeItemsQuery,
  parseMoneyToCents,
  queryKeys,
  recipeCost,
  setRecipeItemMutation,
} from "@comandero/shared";
import type {
  Branch,
  Ingredient,
  MenuProduct,
  Organization,
  RecipeItem,
} from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Select, toSelectOptions } from "@/components/ui/select";
import { TextField } from "@fleet/ui";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";

interface InventoryManagerProps {
  org: () => Organization | null;
  branch: () => Branch | null;
}

export function InventoryManager(props: InventoryManagerProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  const [ingName, setIngName] = createSignal("");
  const [ingStock, setIngStock] = createSignal("");
  const [ingCost, setIngCost] = createSignal("");
  const [recProduct, setRecProduct] = createSignal("");
  const [recIngredient, setRecIngredient] = createSignal("");
  const [recQty, setRecQty] = createSignal("");

  const orgId = (): string | null => props.org()?.id ?? null;

  const ingredients = createPowerSyncQuery<Ingredient>({
    queryKey: () => queryKeys.comandero.ingredients(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getIngredientsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const products = createPowerSyncQuery<MenuProduct>({
    queryKey: () => queryKeys.comandero.products(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getProductsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const recipe = createPowerSyncQuery<RecipeItem>({
    queryKey: () => queryKeys.comandero.recipeItems(recProduct() || "none"),
    queryFn: () => {
      const d = drizzle();
      const p = recProduct();
      return d && p ? getRecipeItemsQuery(d, p) : null;
    },
    db,
    enabled: () => Boolean(recProduct()),
  });

  const ingredientName = (id: string | null | undefined): string =>
    ingredients.data?.find((i) => i.id === id)?.name ?? "—";

  const ingredientCost = (id: string | null | undefined): number =>
    ingredients.data?.find((i) => i.id === id)?.cost ?? 0;

  // Food-cost for selected recipe product: cost = Σ(qty × ingredient
  // cost) and food-cost % = cost / selling price (PURE calculation in TS).
  const selectedProduct = createMemo<MenuProduct | null>(
    () => products.data?.find((p) => p.id === recProduct()) ?? null,
  );

  const recipeCostCents = createMemo<number>(() =>
    recipeCost(
      (recipe.data ?? []).map((r) => ({
        qty: r.qty ?? 0,
        cost: ingredientCost(r.ingredient_id),
      })),
    ),
  );

  const recipeFoodCostPct = createMemo<number>(() =>
    foodCostPercent(recipeCostCents(), selectedProduct()?.price ?? 0),
  );

  async function addIngredient(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    if (!d || !id || !ingName().trim()) {
      return;
    }
    const stock = Number.parseInt(ingStock() || "0", 10);
    await createIngredientMutation(d, id, ingName(), {
      stock: Number.isFinite(stock) ? stock : 0,
      cost: parseMoneyToCents(ingCost()),
    });
    setIngName("");
    setIngStock("");
    setIngCost("");
  }

  async function addRecipeItem(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const id = orgId();
    const p = recProduct();
    const ing = recIngredient();
    const qty = Number.parseInt(recQty() || "0", 10);
    if (!d || !id || !p || !ing || !Number.isFinite(qty) || qty <= 0) {
      return;
    }
    await setRecipeItemMutation(d, id, p, ing, qty);
    setRecQty("");
  }

  async function removeIngredient(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    try {
      await deleteIngredientMutation(d, id);
    } catch (err: unknown) {
      console.error("Error deleting ingredient:", err);
    }
  }

  return (
    <div class="flex flex-col gap-8">
      {/* Ingredients + stock */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Ingredients`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addIngredient}>
          <TextField
            data-testid="ingredient-name-input"
            placeholder={t(msg`Ingredient name`)}
            value={ingName()}
            onInput={(e) => setIngName(e.currentTarget.value)}
          />
          <TextField
            data-testid="ingredient-stock-input"
            placeholder={t(msg`Stock`)}
            inputmode="numeric"
            value={ingStock()}
            onInput={(e) => setIngStock(e.currentTarget.value)}
          />
          <TextField
            data-testid="ingredient-cost-input"
            placeholder={t(msg`Unit cost`)}
            inputmode="decimal"
            value={ingCost()}
            onInput={(e) => setIngCost(e.currentTarget.value)}
          />
          <Button data-testid="add-ingredient-btn" type="submit">
            {t(msg`Add ingredient`)}
          </Button>
        </form>
        <ul class="flex flex-col gap-2">
          <For each={ingredients.data}>
            {(i) => (
              <li
                class="flex items-center justify-between gap-3 rounded-md border border-border/40 px-3 py-2 text-sm"
                data-testid="ingredient-item"
                data-ingredient-id={i.id}
              >
                <span data-testid="ingredient-row-name">{i.name}</span>
                <span class="text-muted-foreground" data-testid="ingredient-row-cost">
                  {formatMoney(i.cost)}
                </span>
                <span class="text-muted-foreground" data-testid="ingredient-row-stock">
                  {i.stock ?? 0}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="delete-ingredient-btn"
                  onClick={() => void removeIngredient(i.id)}
                >
                  {t(msg`Delete`)}
                </Button>
              </li>
            )}
          </For>
        </ul>
      </section>

      {/* Recipes (BOM) */}
      <section>
        <h2 class="mb-3 text-lg font-semibold">{t(msg`Recipes`)}</h2>
        <form class="mb-3 flex flex-wrap gap-2" onSubmit={addRecipeItem}>
          <Select
            data-testid="recipe-product-select"
            aria-label={t(msg`Product`)}
            placeholder={t(msg`Select product`)}
            value={recProduct()}
            onChange={setRecProduct}
            options={toSelectOptions(
              products.data,
              (p) => p.id,
              (p) => p.name,
            )}
          />
          <Select
            data-testid="recipe-ingredient-select"
            aria-label={t(msg`Ingredient`)}
            placeholder={t(msg`Select ingredient`)}
            value={recIngredient()}
            onChange={setRecIngredient}
            options={toSelectOptions(
              ingredients.data,
              (i) => i.id,
              (i) => i.name,
            )}
          />
          <TextField
            data-testid="recipe-qty-input"
            placeholder={t(msg`Qty per unit`)}
            inputmode="numeric"
            value={recQty()}
            onInput={(e) => setRecQty(e.currentTarget.value)}
          />
          <Button data-testid="add-recipe-item-btn" type="submit">
            {t(msg`Add to recipe`)}
          </Button>
        </form>
        <Show when={recProduct()}>
          <ul class="flex flex-col gap-1" data-testid="recipe-items">
            <For each={recipe.data}>
              {(r) => (
                <li class="flex justify-between text-sm" data-testid="recipe-item-row">
                  <span>{ingredientName(r.ingredient_id)}</span>
                  <span class="text-muted-foreground">
                    ×{r.qty ?? 0} · {formatMoney((r.qty ?? 0) * ingredientCost(r.ingredient_id))}
                  </span>
                </li>
              )}
            </For>
          </ul>

          {/* Food-cost del producto: costo de la receta y % sobre el precio. */}
          <div
            class="mt-3 grid grid-cols-3 gap-3 border-t border-border/40 pt-3 text-sm"
            data-testid="food-cost-panel"
          >
            <div>
              <div class="text-xs text-muted-foreground">{t(msg`Recipe cost`)}</div>
              <div class="font-semibold" data-testid="food-cost-amount">
                {formatMoney(recipeCostCents())}
              </div>
            </div>
            <div>
              <div class="text-xs text-muted-foreground">{t(msg`Sale price`)}</div>
              <div class="font-semibold" data-testid="food-cost-price">
                {formatMoney(selectedProduct()?.price ?? 0)}
              </div>
            </div>
            <div>
              <div class="text-xs text-muted-foreground">{t(msg`Food cost %`)}</div>
              <div class="font-semibold" data-testid="food-cost-percent">
                {recipeFoodCostPct()}%
              </div>
            </div>
          </div>
        </Show>
      </section>
    </div>
  );
}
